import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysCompany } from './queryKeys';

/**
 * Fetches company logo from the API.
 * @param companyId - The ID of the company to fetch the logo for.
 * @returns The company logo as a Blob.
 * @throws Error if the API request fails.
 */
export const fetchCompanyLogo = async (companyId: UUID) => {
  return await vestigasApi.get(ENDPOINT.COMPANY.GET_LOGO(companyId)).blob();
};

/**
 * React Query based custom hook for getting the logo for a company with a given companyId.
 * @param companyId - The ID of the company to fetch the logo for.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export const useQueryCompanyLogo = <TSelected = string | undefined>(
  companyId: UUID,
  options: Omit<
    UseQueryOptions<string | undefined, unknown, TSelected>,
    'queryKey' | 'queryFn'
  > = {},
) =>
  useQuery<Blob, unknown, TSelected>({
    enabled: Boolean(companyId),
    gcTime: ms('60m'),
    queryFn: async () => fetchCompanyLogo(companyId),
    queryKey: queryKeysCompany.getLogo(companyId),
    select: (logoData) =>
      logoData.size > 0 ? URL.createObjectURL(logoData) : undefined,
    staleTime: Infinity, // Don't refetch a cached file
    throwOnError: false,
    ...options,
  });
